/**
 * @file: useLeadDiscussion.ts
 * @copyright: (c) 2019-2026 sichuan zhichetech co., ltd.
 *
 * State for the lead's internal-discussion thread — a blog-comment-style list
 * with no realtime/unread. Fetches on mount + screen focus, posts optimistically
 * (a temp row appears immediately, is replaced by the server view on success and
 * dropped on failure), and deletes through the service. No polling, no sockets.
 */

import { useCurrentUser } from '@euler/app/flows/auth/context';
import { LeadMediaAttachmentInput } from '@euler/model/lead/attachment';
import { LeadMessageView } from '@euler/model/lead/types';
import { useServiceFactory } from '@euler/services/factory';
import { useFocusEffect } from '@react-navigation/native';
import { nanoid } from 'nanoid/non-secure';
import { useCallback, useRef, useState } from 'react';

const kPageSize = 50;

// Hydrate an optimistic message body into the same shape the read API returns,
// so the row renders identically before the server echoes it back. The temp
// attachment ids are negative placeholders (never collide with real ids).
const toOptimisticAttachments = (inputs: LeadMediaAttachmentInput[]) =>
  inputs.map((input, index) => ({
    ...input,
    id: -(index + 1),
    coverUrl: input.coverUrl ?? null,
    sortOrder: index,
  }));

export const useLeadDiscussion = (leadId: string) => {
  const { leadService } = useServiceFactory();
  const currentUser = useCurrentUser();
  const [messages, setMessages] = useState<LeadMessageView[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);
  // Monotonic load token: only the latest-issued load may apply its result, so
  // a stale reload (e.g. the focus refetch fired when the confirm dialog pops,
  // started before a delete committed) can't resurrect a removed message.
  const loadSeq = useRef(0);

  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    setLoading(true);
    try {
      // The API returns the latest page newest-first; display oldest→newest so
      // new posts (appended at the bottom) read in chronological order.
      const list = await leadService.listMessages(leadId, 0, kPageSize);
      if (seq !== loadSeq.current) return;
      setMessages([...list].reverse());
      setError(undefined);
    } catch (e) {
      if (seq === loadSeq.current) setError(e as Error);
    } finally {
      if (seq === loadSeq.current) {
        setLoading(false);
        setLoaded(true);
      }
    }
  }, [leadId, leadService]);

  // Runs on mount and on every focus (returning from a sub-screen), so the
  // thread stays fresh without polling.
  useFocusEffect(
    useCallback(() => {
      void load();
    }, [load]),
  );

  const post = useCallback(
    async (body: string, attachments: LeadMediaAttachmentInput[]) => {
      const clientMsgId = nanoid(32);
      const trimmed = body.trim();
      const temp: LeadMessageView = {
        id: `temp:${clientMsgId}`,
        leadId,
        author: {
          id: currentUser?.user.memberId ?? 0,
          name: currentUser?.user.name ?? '',
          avatar: currentUser?.user.avatar ?? null,
        },
        body: trimmed || null,
        attachments: toOptimisticAttachments(attachments),
        createdAt: new Date(),
      };
      setMessages(prev => [...prev, temp]);
      try {
        const saved = await leadService.sendMessage(leadId, {
          body: trimmed || null,
          clientMsgId,
          attachments: attachments.length ? attachments : undefined,
        });
        // Replace the temp row in place; if a focus-reload wiped it while the
        // request was in flight (and the server view isn't already present),
        // append the saved row so the post never silently vanishes.
        setMessages(prev =>
          prev.some(m => m.id === temp.id)
            ? prev.map(m => (m.id === temp.id ? saved : m))
            : prev.some(m => m.id === saved.id)
            ? prev
            : [...prev, saved],
        );
      } catch (e) {
        setMessages(prev => prev.filter(m => m.id !== temp.id));
        throw e;
      }
    },
    [currentUser, leadId, leadService],
  );

  const remove = useCallback(
    async (id: string) => {
      // Optimistically drop the row; the updater stays pure (no outer-var
      // side effect).
      setMessages(prev => prev.filter(m => m.id !== id));
      try {
        await leadService.deleteMessage(leadId, id);
        // Resync after the commit — this becomes the latest load, so it (not a
        // stale concurrent reload) defines the final list.
        void load();
      } catch (e) {
        void load();
        throw e;
      }
    },
    [leadId, leadService, load],
  );

  return { messages, loading, loaded, error, load, post, remove };
};
